import React, { useCallback, useEffect, useState } from 'react';
import Drawer from '../common/Drawer';
import BeaconManageModal from './BeaconManageModal';
import { getBeacons } from '../../api/apartments';
import { Beacon } from '../../types';
import { toast } from '../common/Toast';

interface BeaconSidePanelProps {
  isOpen: boolean;
  apartmentId: string | null;
  apartmentName: string;
  onClose: () => void;
}

const BeaconSidePanel: React.FC<BeaconSidePanelProps> = ({
  isOpen,
  apartmentId,
  apartmentName,
  onClose,
}) => {
  const [beacons, setBeacons] = useState<Beacon[]>([]);
  const [loading, setLoading] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const fetchBeacons = useCallback(async () => {
    if (!apartmentId) return;
    setLoading(true);
    try {
      const res = await getBeacons(apartmentId);
      setBeacons(res);
    } catch {
      toast.error('비콘 목록을 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  }, [apartmentId]);

  useEffect(() => {
    if (isOpen && apartmentId) {
      fetchBeacons();
    }
  }, [isOpen, apartmentId, fetchBeacons]);

  const handleManageClose = () => {
    setManageOpen(false);
  };

  const handleManageUpdate = () => {
    fetchBeacons();
  };

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} title={`${apartmentName} 비콘`}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            로딩 중...
          </div>
        ) : beacons.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ color: '#999', marginBottom: 16 }}>등록된 비콘이 없습니다</p>
            <button
              className="btn btn--primary"
              onClick={() => setManageOpen(true)}
            >
              비콘 등록
            </button>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {beacons.map((b) => (
                <div
                  key={b.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  <span className={`badge ${b.type === 'entry' ? 'badge--green' : 'badge--orange'}`}>
                    {b.type === 'entry' ? '입차' : '출차'}
                  </span>
                  <span style={{ fontWeight: 500, fontSize: 14 }}>
                    HW {b.beacon_hw_id}
                  </span>
                  {b.name && (
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
                      {b.name}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: b.battery_low ? '#F44336' : '#4CAF50',
                      }}
                      title={b.battery_low ? '배터리 부족' : '배터리 정상'}
                    />
                  </span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 20, textAlign: 'center' }}>
              <button
                className="btn btn--primary"
                onClick={() => setManageOpen(true)}
              >
                비콘 관리
              </button>
            </div>
          </div>
        )}
      </Drawer>

      {apartmentId && (
        <BeaconManageModal
          isOpen={manageOpen}
          onClose={handleManageClose}
          apartmentId={apartmentId}
          onUpdate={handleManageUpdate}
        />
      )}
    </>
  );
};

export default BeaconSidePanel;
